import { Link, matchPath, useLocation } from "react-router-dom";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@components/ui/sidebar";
import {
  BadgeEuro,
  BookOpen,
  HandCoins,
  Hexagon,
  CircleGauge,
  LayoutList,
  BadgeAlert,
  GraduationCap,
  Building2,
  FileSignature,
  User,
  Users,
  BookUser,
  LibraryBig,
} from "lucide-react";
import { PendingCountBadge } from "@shared-kernel/primary/shared/pending-count-badge";
import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { ROLE_BASED_URLS } from "src/routes";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { StudentVM, StudentPendingActionsVM } from "@shared-kernel/application/ports/student/student-repository";
import { useAppSelector } from "@redux/hooks";
import { selectTeacherProfileRetrieval } from "@user-management-context/read/application/use-cases/teacher/teacher-profile-retrieval/selectors/teacher-profile-selectors";
import { TEACHER_PAYOUT_MODE, TEACHER_STANDING } from "@user-management-context/read/domain/types/admin/teacher";
import { useTeacherStanding } from "src/hooks/use-teacher-standing";
import { getTeacherProfileCompleteness } from "@user-management-context/primary/teacher/teacher-user-detail/teacher-profile-completeness";

interface NavListItem {
  title: string;
  url: string;
  icon: ReactElement;
}

const EVALUATIONS_MENU_LABEL = "Évaluations";
const FUNDING_MENU_LABEL = "Financements";
const REFUND_REQUEST_MENU_LABEL = "Remboursement de frais";
const ATTENDANCE_SHEETS_MENU_LABEL = "Émargements";
const TRAINING_ITEMS_MENU_LABEL = "Formations";
const MASTERCLASS_MENU_LABEL = "Masterclass";
const EXTERNAL_TEACHERS_MENU_LABEL = "Intervenants externes";

const studentNavList: NavListItem[] = [
  {
    title: "Tableau de bord",
    url: ROLE_BASED_URLS[ROLES.STUDENT]["dashboard"],
    icon: <CircleGauge />,
  },
  {
    title: ATTENDANCE_SHEETS_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.STUDENT]["attendanceSheet"].list,
    icon: <FileSignature />,
  },
  {
    title: TRAINING_ITEMS_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.STUDENT]["trainingItem"].list,
    icon: <GraduationCap />,
  },
  {
    title: "Mes professeurs",
    url: ROLE_BASED_URLS[ROLES.STUDENT]["teachers"],
    icon: <Users />,
  },
  {
    title: REFUND_REQUEST_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.STUDENT]["refundRequest"].base,
    icon: <HandCoins />,
  },
  {
    title: FUNDING_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.STUDENT]["fundingRequest"].list,
    icon: <BadgeEuro />,
  },
  {
    title: "Signalements",
    url: ROLE_BASED_URLS[ROLES.STUDENT]["issue"].base,
    icon: <BadgeAlert />,
  },
  {
    title: "Ressources",
    url: ROLE_BASED_URLS[ROLES.STUDENT]["resources"],
    icon: <LibraryBig />,
  },
];

const teacherNavList: NavListItem[] = [
  {
    title: "Tableau de bord",
    url: ROLE_BASED_URLS[ROLES.TEACHER]["dashboard"],
    icon: <CircleGauge />,
  },
  {
    title: EVALUATIONS_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.TEACHER]["evaluations"].listGroupedByStudent,
    icon: <Hexagon />,
  },
  {
    title: "Historique des cours",
    url: ROLE_BASED_URLS[ROLES.TEACHER]["lesson"].list,
    icon: <BookOpen />,
  },
  {
    title: "Élèves",
    url: ROLE_BASED_URLS[ROLES.TEACHER]["students"].list,
    icon: <GraduationCap />,
  },
  {
    title: MASTERCLASS_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.TEACHER]["batch"].list,
    icon: <LayoutList />,
  },
  {
    title: "Paiements",
    url: ROLE_BASED_URLS[ROLES.TEACHER]["payout"].list,
    icon: <BadgeEuro />,
  },
  {
    title: "Mes Studios",
    url: ROLE_BASED_URLS[ROLES.TEACHER]["studio"].list,
    icon: <Building2 />,
  },
  {
    title: EXTERNAL_TEACHERS_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.TEACHER]["externalTeachers"].list,
    icon: <BookUser />,
  },
  {
    title: "Signalements",
    url: ROLE_BASED_URLS[ROLES.TEACHER]["issue"].base,
    icon: <BadgeAlert />,
  },
  {
    title: "Profil",
    url: ROLE_BASED_URLS[ROLES.TEACHER]["profile"],
    icon: <User />,
  },
  {
    title: "Ressources",
    url: ROLE_BASED_URLS[ROLES.TEACHER]["resources"],
    icon: <LibraryBig />,
  },
];

const providerNavList: NavListItem[] = [
  {
    title: "Tableau de bord",
    url: ROLE_BASED_URLS[ROLES.PROVIDER]["dashboard"],
    icon: <CircleGauge />,
  },
  {
    title: MASTERCLASS_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.PROVIDER]["batch"].list,
    icon: <LayoutList />,
  },
  {
    title: EVALUATIONS_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.PROVIDER]["evaluations"].listGroupedByStudent,
    icon: <Hexagon />,
  },
  {
    title: "Mes Studios",
    url: ROLE_BASED_URLS[ROLES.PROVIDER]["studio"].list,
    icon: <Building2 />,
  },
  {
    title: EXTERNAL_TEACHERS_MENU_LABEL,
    url: ROLE_BASED_URLS[ROLES.PROVIDER]["externalTeachers"].list,
    icon: <BookUser />,
  },
  {
    title: "Signalements",
    url: ROLE_BASED_URLS[ROLES.PROVIDER]["issue"].base,
    icon: <BadgeAlert />,
  },
  {
    title: "Profil",
    url: ROLE_BASED_URLS[ROLES.PROVIDER]["profile"],
    icon: <User />,
  },
];

const navListByRole: Record<ROLES, NavListItem[]> = {
  [ROLES.STUDENT]: studentNavList,
  [ROLES.TEACHER]: teacherNavList,
  [ROLES.PROVIDER]: providerNavList,
};

interface Props {
  role: Nullable<ROLES>;
  student: Nullable<StudentVM>;
  studentPendingActions: StudentPendingActionsVM;
  educationalAdvisorEvaluationPendingCount: number;
  educationalAdvisorBatchPendingCount: number;
}

export const StandardUserSideBar = ({
  role,
  student,
  studentPendingActions,
  educationalAdvisorEvaluationPendingCount,
  educationalAdvisorBatchPendingCount,
}: Props) => {
  const location = useLocation();
  const [navList, setNavList] = useState<NavListItem[]>([]);
  const { data: teacher } = useAppSelector(selectTeacherProfileRetrieval);
  const { hasMinimumStanding } = useTeacherStanding();
  const { isMobile, toggleSidebar } = useSidebar();

  useEffect(() => {
    let list: NavListItem[] = [];

    if (role) {
      list = navListByRole[role];

      if (role === ROLES.STUDENT && student && !student.canRequestARefund) {
        list = list.filter(r => !r.title.match(REFUND_REQUEST_MENU_LABEL));
      }

      if (role === ROLES.TEACHER && teacher && teacher.payoutMode !== TEACHER_PAYOUT_MODE.ON_DEMAND) {
        list = list.filter(r => r.title !== "Paiements");
      }

      if (role === ROLES.TEACHER && !hasMinimumStanding(TEACHER_STANDING.TRUSTED)) {
        list = list.filter(r => r.title !== MASTERCLASS_MENU_LABEL && r.title !== EXTERNAL_TEACHERS_MENU_LABEL);
      }
    }
    setNavList(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasMinimumStanding is derived from `teacher` (already a dep); including it causes infinite re-renders since it's a new ref each render
  }, [role, student, teacher]);

  const teacherProfileMissingCount = role === ROLES.TEACHER && teacher ? getTeacherProfileCompleteness(teacher).totalMissingCount : 0;

  return (
    <SidebarMenu className="px-2">
      {navList.map(item => {
        // Check if the current path matches the item URL, including subpaths
        const isFundingMenuItem = item.title === FUNDING_MENU_LABEL;
        const matchesFundingRequestForm = isFundingMenuItem && Boolean(matchPath("/funding-request/*", location.pathname));
        const isActive = Boolean(matchPath(`/${item.url}/*`, location.pathname)) || matchesFundingRequestForm;
        // Calculate pending count for badge display
        let pendingCount = 0;
        if (item.title === ATTENDANCE_SHEETS_MENU_LABEL) {
          pendingCount = studentPendingActions.attendanceSheets.pendingCount;
        } else if (item.title === TRAINING_ITEMS_MENU_LABEL) {
          pendingCount = studentPendingActions.surveys.pendingCount + studentPendingActions.objectives.pendingCount;
        } else if (item.title === EVALUATIONS_MENU_LABEL && role !== ROLES.STUDENT) {
          pendingCount = educationalAdvisorEvaluationPendingCount;
        } else if (item.title === MASTERCLASS_MENU_LABEL && role !== ROLES.STUDENT) {
          pendingCount = educationalAdvisorBatchPendingCount;
        }
        const profileMissingCount = role === ROLES.TEACHER && item.title === "Profil" ? teacherProfileMissingCount : 0;

        return (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton asChild isActive={isActive} tooltip={item.title} hidden={false}>
              <Link
                to={item.url}
                onClick={() => {
                  if (isMobile) toggleSidebar();
                }}
              >
                {item.icon}
                <span className="flex-1">{item.title}</span>
                <PendingCountBadge count={pendingCount + profileMissingCount} className="ml-auto" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
};
